import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Overline, SectionHeading } from "@/components/Section";
import PlatformCard from "@/components/PlatformCard";

function Divider({ label, count, purple }) {
  return (
    <div className="flex items-center gap-4 mb-10">
      <Overline className={purple ? "!text-tib-royal" : ""}>{label}</Overline>
      <span className="flex-1 h-px bg-zinc-900" />
      <span className="text-xs font-mono text-zinc-600">{count}</span>
    </div>
  );
}

export default function Platforms() {
  const [internal, setInternal] = useState([]);
  const [external, setExternal] = useState([]);

  useEffect(() => {
    api.get("/public/platforms.json").then((r) => {
      setInternal(r.data.internal || []);
      setExternal(r.data.external || []);
    }).catch(() => {});
  }, []);

  const iLive = internal.filter((p) => p.status === "live");
  const iSoon = internal.filter((p) => p.status === "coming_soon");
  const eLive = external.filter((p) => p.status === "live");
  const eSoon = external.filter((p) => p.status === "coming_soon");

  return (
    <div data-testid="platforms-page">
      <section className="section-pad border-b border-zinc-900">
        <div className="container-tib">
          <SectionHeading
            overline="The portfolio"
            title="Platforms, websites & apps TIB is building."
            body="Internal platforms are owned by MPGBF and operated by TIB. External platforms are built under contract — clients keep ownership of their assets, or partner with TIB to operate them. Everything below links to its build page; live platforms link straight through."
          />
        </div>
      </section>

      {/* INTERNAL */}
      <section className="section-pad">
        <div className="container-tib">
          <div className="mb-14">
            <h2 className="h-serif text-3xl text-white">Internal — MPGBF-owned</h2>
            <p className="text-zinc-500 mt-2">Built by TIB for the holding company, and operated by TIB.</p>
          </div>

          <Divider label="Live" count={iLive.length} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="internal-live-grid">
            {iLive.map((p) => <PlatformCard key={p.slug} platform={p} />)}
          </div>

          <Divider label="Coming soon" count={iSoon.length} />
          {iSoon.length === 0 ? (
            <p className="text-zinc-500">More internal builds are on the way.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="internal-soon-grid">
              {iSoon.map((p) => <PlatformCard key={p.slug} platform={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* EXTERNAL */}
      <section className="section-pad pt-0">
        <div className="container-tib">
          <div className="mb-14">
            <h2 className="h-serif text-3xl text-tib-royal">External — client-owned</h2>
            <p className="text-zinc-500 mt-2">Built under contract. Clients keep their assets, or partner with TIB to manage them.</p>
          </div>

          <Divider label="Live" count={eLive.length} purple />
          {eLive.length === 0 ? (
            <div className="card-edge p-12 text-center mb-16" data-testid="external-empty">
              <p className="text-zinc-400 text-lg">
                Client platform slots are open. <span className="text-white">Yours could be the first.</span>
              </p>
              <Link to="/clients/apply" className="btn-primary mt-6 inline-flex">Apply to build →</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="external-live-grid">
              {eLive.map((p) => <PlatformCard key={p.slug} platform={p} />)}
            </div>
          )}

          <Divider label="Coming soon" count={eSoon.length} purple />
          {eSoon.length === 0 ? (
            <div className="card-edge p-10 text-center" data-testid="external-soon-empty">
              <p className="text-zinc-400">
                Client builds in progress will appear here. <Link to="/clients/apply" className="text-tib-royal link-underline">Start yours →</Link>
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="external-soon-grid">
              {eSoon.map((p) => <PlatformCard key={p.slug} platform={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
